import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Stack, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, TablePagination, Chip, Avatar, IconButton, Tooltip, Button,
    TextField, Select, MenuItem, InputAdornment, Checkbox, LinearProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Skeleton,
    FormControl, InputLabel, Divider
} from '@mui/material';
import {
    Search as SearchIcon,
    Visibility as ReviewIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    CalendarToday as CalendarIcon,
    FolderOpen as EmptyIcon,
    FilterList as FilterIcon,
    Close as CloseIcon,
    FileDownload as ExportIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    UnfoldMore as ExpandAllIcon,
    UnfoldLess as CollapseAllIcon,
} from '@mui/icons-material';
import TimesheetReview from './timesheetReview';
import { getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { getUserRole } from '../../../utils/auth';

// ─── Constants ────────────────────────────────────────────────────────────────
const MONTHS      = ['All', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const _cy         = new Date().getFullYear();
const YEARS       = [String(_cy - 2), String(_cy - 1), String(_cy)];
const ALL_STATUSES = ['All', 'Draft', 'Submitted', 'Approved', 'Rejected', 'Pending Level 2'];

const STATUS_CFG = {
    Draft:             { color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' },
    Submitted:         { color: '#1976d2', bg: '#e3f2fd', border: '#90caf9' },
    Approved:          { color: '#00796b', bg: '#E0F2F1', border: '#80cbc4' },
    Rejected:          { color: '#d32f2f', bg: '#FFEBEE', border: '#ef9a9a' },
    'Pending Level 2': { color: '#c2410c', bg: '#fff7ed', border: '#fdba74' },
};
// Distinct avatar colours cycling through indigo palette
const AVATAR_COLORS = ['#4f46e5', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626', '#db2777'];

function StatusChip({ status }) {
    const cfg = STATUS_CFG[status] || STATUS_CFG.Draft;
    const label = status === 'Pending Level 2' ? 'Pending External Approval' : status;
    return (
        <Chip label={label} size="small" sx={{
            fontWeight: 700, fontSize: '0.68rem',
            bgcolor: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.border}`,
        }} />
    );
}

function ResubmittedBadge({ count, status }) {
    if (!count || status !== 'Submitted') return null;
    return (
        <Chip label="Resubmitted" size="small" sx={{
            fontWeight: 700, fontSize: '0.68rem', ml: 0.5,
            bgcolor: '#eff6ff', color: '#1d4ed8',
            border: '1px solid #93c5fd',
        }} />
    );
}

function fmtDate(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtShort(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// ─── Bulk Reject Dialog ───────────────────────────────────────────────────────
function BulkRejectDialog({ open, count, onConfirm, onClose }) {
    const [comment, setComment] = useState('');
    const [err, setErr]         = useState('');
    const handleSubmit = () => {
        if (!comment.trim()) { setErr('Comment is required when rejecting.'); return; }
        onConfirm(comment.trim());
        setComment(''); setErr('');
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                    <Typography fontWeight={700}>Reject {count} Timesheet{count !== 1 ? 's' : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">A comment is required for rejection</Typography>
                </Box>
                <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
                <TextField fullWidth multiline rows={3}
                    placeholder="Enter rejection reason for all selected timesheets…"
                    value={comment} onChange={e => { setComment(e.target.value); setErr(''); }}
                    error={!!err} helperText={err}
                    sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit}
                    sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                    Reject All Selected
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TeamTimesheetList({
    timesheets, teamMembers = [], loading = false,
    filterStatus, onAction, onBulkApprove, onBulkReject,
}) {
    // ── Filters ──
    const [search,       setSearch]       = useState('');
    const [employee,     setEmployee]     = useState('All');
    const [year,         setYear]         = useState(String(new Date().getFullYear()));
    const [month,        setMonth]        = useState('All');
    const [statusFilter, setStatusFilter] = useState(
        filterStatus === 'Submitted'       ? 'Submitted'
        : filterStatus === 'Pending Level 2' ? 'Pending Level 2'
        : filterStatus === 'Rejected' ? 'Rejected'
        : filterStatus === 'Approved' ? 'Approved'
        : 'All'
    );
    const [page,        setPage]        = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // ── Selection & dialogs ──
    const [selected,       setSelected]       = useState([]);
    const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
    const navigate = useNavigate();
    const [reviewTs,       setReviewTs]       = useState(null);
    const [snack,          setSnack]          = useState({ open: false, msg: '', severity: 'success' });

    // ── Global config ──
    const [globalConfig, setGlobalConfig] = useState(null);
    useEffect(() => {
        getData(API_ENDPOINTS.getTimesheetConfig())
            .then(res => setGlobalConfig(res?.result?.data ?? res?.result ?? null))
            .catch(() => {});
    }, []);

    // ── Expand / collapse (empty Set = all expanded) ──
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    const isExpanded  = (id) => !collapsedGroups.has(id);
    const role = getUserRole();
    const toggleGroup = (id) => setCollapsedGroups(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // ── Filtered list ──
    const filtered = useMemo(() => timesheets.filter(ts => {
        const monthIndex = MONTHS.indexOf(month);
        const tsMonth    = ts.weekStart ? new Date(ts.weekStart).getMonth() + 1 : -1;
        if (search       && !ts.employeeName.toLowerCase().includes(search.toLowerCase())) return false;
        if (employee !== 'All' && String(ts.employeeId) !== String(employee)) return false;
        if (year         && ts.weekStart && !ts.weekStart.startsWith(year)) return false;
        if (month !== 'All' && tsMonth !== monthIndex) return false;
        if (statusFilter !== 'All' && ts.status !== statusFilter) return false;
        return true;
    }), [timesheets, search, employee, year, month, statusFilter]);

    // ── Group by employee ──
    const groups = useMemo(() => {
        const map = {};
        filtered.forEach(ts => {
            const key = String(ts.employeeId);
            if (!map[key]) {
                map[key] = {
                    employeeId:   key,
                    employeeName: ts.employeeName,
                    employeeAvatar: ts.employeeAvatar,
                    employeeDept: ts.employeeDept,
                    employeeTeam: ts.employeeTeam,
                    timesheets:   [],
                };
            }
            map[key].timesheets.push(ts);
        });
        return Object.values(map);
    }, [filtered]);

    const paginatedGroups = useMemo(
        () => groups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [groups, page, rowsPerPage]
    );

    // ── Expand-all / collapse-all ──
    const noneCollapsed = groups.every(g => !collapsedGroups.has(g.employeeId));
    const toggleAllGroups = () => {
        if (noneCollapsed) setCollapsedGroups(new Set(groups.map(g => g.employeeId)));
        else setCollapsedGroups(new Set());
    };

    // ── Selection helpers ──
    // isActionableStatus: controls whether approve/reject buttons appear.
    // Also hides them when the system will auto-approve this timesheet on its
    // own (COMPANY scope org-wide auto-approval, or PROJECT scope where every
    // project on it has auto-approval enabled) — the manager never has to act.
    const isActionableStatus = (ts) => ts?.status === 'Submitted' && !ts?.willAutoApprove;

    // All IDs in group — any status can be selected (checkboxes / export)
    const groupAllIds = (g) => g.timesheets.map(ts => ts.id);

    const isGroupAllSelected = (g) => {
        const ids = groupAllIds(g);
        return ids.length > 0 && ids.every(id => selected.includes(id));
    };
    const isGroupPartial = (g) => {
        const ids = groupAllIds(g);
        return ids.some(id => selected.includes(id)) && !isGroupAllSelected(g);
    };
    const toggleGroupSelection = (g) => {
        const ids = groupAllIds(g);
        if (isGroupAllSelected(g)) setSelected(prev => prev.filter(id => !ids.includes(id)));
        else setSelected(prev => [...new Set([...prev, ...ids])]);
    };

    const allPageIds     = paginatedGroups.flatMap(groupAllIds);
    const allPageSelected = allPageIds.length > 0 && allPageIds.every(id => selected.includes(id));
    const somePageSelected = allPageIds.some(id => selected.includes(id));
    const toggleAll = () => {
        if (allPageSelected) setSelected(prev => prev.filter(id => !allPageIds.includes(id)));
        else setSelected(prev => [...new Set([...prev, ...allPageIds])]);
    };

    const toggleSelect = (id) =>
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const someSelected = selected.length > 0;
    // Count of selected that are actually actionable (for bulk action label)
    const selectedActionableCount = selected.filter(id => {
        const ts = timesheets.find(t => t.id === id);
        return ts && isActionableStatus(ts);
    }).length;

    // ── Reset ──
    const handleReset = () => {
        setSearch(''); setEmployee('All'); setYear(String(new Date().getFullYear())); setMonth('All');
        setStatusFilter(
            filterStatus === 'Submitted'       ? 'Submitted'
            : filterStatus === 'Pending Level 2' ? 'Pending Level 2'
            : 'All'
        );
        setPage(0); setSelected([]);
    };

    // ── CSV Export ──
    const handleExportCSV = () => {
        const exportData = someSelected ? timesheets.filter(ts => selected.includes(ts.id)) : filtered;
        if (!exportData.length) return;
        const headers = ['Employee Name','Department','ID','Team','Week Start','Week End','Total Hours','OT Hours','Billable Hours','Billable %','Project Count','Status','Submitted On'];
        const rows = exportData.map(ts => {
            const rate = ts.totalHours > 0 ? Math.round((ts.billableHours / ts.totalHours) * 100) : 0;
            return [ts.employeeName, ts.employeeDept, ts.employeeCode, ts.team_name || '', ts.weekStart, ts.weekEnd, ts.totalHours, ts.overtimeHours, ts.billableHours, `${rate}%`, ts.projectCount, ts.status, ts.submittedOn || 'Not submitted'];
        });
        const summary = [
            `# Exported: ${new Date().toLocaleString('en-GB')}`,
            `# Filters — Employee: ${employee === 'All' ? 'All' : teamMembers.find(m => String(m.id) === String(employee))?.name || employee} | Year: ${year} | Month: ${month} | Status: ${statusFilter}`,
            someSelected ? `# Scope: Selected (${exportData.length} of ${filtered.length})` : `# Scope: All filtered`,
        ].join('\n');
        const csv = [summary, headers.map(h => `"${h}"`).join(','), ...rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(','))].join('\n');
        const link = document.createElement('a');
        link.href     = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        link.download = `timesheets-${year}-${month.toLowerCase()}-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        setSnack({ open: true, msg: `${exportData.length} timesheet(s) exported.`, severity: 'success' });
    };

    // ── Actions ──
    const handleQuickAction = async (ts, decision) => {
        if (decision === 'Reject') { setReviewTs(ts); return; }
        try {
            if (ts.entries.some(entry => ['PENDING', 'REJECTED'].includes(entry.l2_status))){
                setSnack({ open: true, msg: `${ts.employeeName}'s timesheet has pending or rejected entries.`, severity: 'error' });
                return;
            }
            await onAction(ts.id, 'Approve', '');
            setSnack({ open: true, msg: `${ts.employeeName}'s timesheet approved.`, severity: 'success' });
        } catch (error) {
            setSnack({ open: true, msg: error?.response?.data?.message || 'Approve failed. Please try again.', severity: 'error' });
        }
    };

    const handleBulkApprove = async () => {
        const ids = selected.filter(id => { const ts = timesheets.find(t => t.id === id); return ts && isActionableStatus(ts); });
        if (!ids.length) return;
        try {
            if (onBulkApprove) await onBulkApprove(ids);
            else await Promise.all(ids.map(id => onAction(id, 'Approve', 'Bulk approved')));
            setSnack({ open: true, msg: `${ids.length} timesheet(s) approved.`, severity: 'success' });
            setSelected([]);
        } catch {
            setSnack({ open: true, msg: 'Bulk approve failed. Please try again.', severity: 'error' });
        }
    };

    const handleBulkReject = async (comment) => {
        const ids = selected.filter(id => { const ts = timesheets.find(t => t.id === id); return ts && isActionableStatus(ts); });
        if (!ids.length) { setBulkRejectOpen(false); return; }
        try {
            if (onBulkReject) await onBulkReject(ids, comment);
            else await Promise.all(ids.map(id => onAction(id, 'Reject', comment)));
            setSnack({ open: true, msg: `${ids.length} timesheet(s) rejected.`, severity: 'warning' });
            setSelected([]); setBulkRejectOpen(false);
        } catch {
            setSnack({ open: true, msg: 'Bulk reject failed. Please try again.', severity: 'error' });
            setBulkRejectOpen(false);
        }
    };

    const handleReviewAction = async (id, decision, comment) => {
        try {
            await onAction(id, decision, comment);
            setReviewTs(null);
            const msg = decision === 'SendToL2'
                ? 'Entries dispatched for external approval.'
                : `Timesheet ${decision === 'Approve' ? 'approved' : 'rejected'} successfully.`;
            const severity = decision === 'Approve' || decision === 'SendToL2' ? 'success' : 'warning';
            setSnack({ open: true, msg, severity });
        } catch {
            setSnack({ open: true, msg: 'Action failed. Please try again.', severity: 'error' });
        }
    };

    // ── Column span for group header content (all cols except checkbox and expand) ──
    const contentColSpan = 7;

    const selectSx = { fontSize: 13, borderRadius: 1.5, height: 36, bgcolor: '#fff', '& .MuiSelect-select': { py: 0, fontSize: 13 } };
    const labelSx  = { fontSize: 12, lineHeight: 1, '&.MuiInputLabel-shrink': { fontSize: 12 } };

    return (
        <Box>
            {/* ── Filter Bar ── */}
            <Paper elevation={0} sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2.5, p: 1.75, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    <FilterIcon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />

                    {/* Search */}
                    <TextField size="small" placeholder="Search employee…"
                        value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
                        sx={{ flex: { xs: '1 1 100%', sm: '0 1 200px' }, minWidth: { xs: '100%', sm: 160 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: 13, height: 36 }, '& .MuiInputBase-input': { py: 0, fontSize: 13 } }}
                    />

                    {/* Employee */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel sx={labelSx}>Employee</InputLabel>
                        <Select value={employee} label="Employee" onChange={e => { setEmployee(e.target.value); setPage(0); }} sx={selectSx}>
                            <MenuItem value="All" sx={{ fontSize: 13 }}>All Employees</MenuItem>
                            {teamMembers.map(m => <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>{m.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {/* Year */}
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={labelSx}>Year</InputLabel>
                        <Select value={year} label="Year" onChange={e => { setYear(e.target.value); setPage(0); }} sx={selectSx}>
                            {YEARS.map(y => <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {/* Month */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={labelSx}>Month</InputLabel>
                        <Select value={month} label="Month" onChange={e => { setMonth(e.target.value); setPage(0); }} sx={selectSx}>
                            {MONTHS.map(m => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {/* Status */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel sx={labelSx}>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPage(0); }} sx={selectSx}>
                            {ALL_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s === 'Pending Level 2' ? 'Pending External Approval' : s}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {/* Expand / Collapse All */}
                    {groups.length > 0 && (
                        <Tooltip title={noneCollapsed ? 'Collapse all groups' : 'Expand all groups'}>
                            <Button size="small" onClick={toggleAllGroups} startIcon={noneCollapsed ? <CollapseAllIcon sx={{ fontSize: 15 }} /> : <ExpandAllIcon sx={{ fontSize: 15 }} />}
                                sx={{ textTransform: 'none', fontSize: 12, color: '#6366f1', flexShrink: 0,
                                    bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#e0e7ff' }, px: 1.25 }}>
                                {noneCollapsed ? 'Collapse All' : 'Expand All'}
                            </Button>
                        </Tooltip>
                    )}

                    <Button size="small" onClick={handleReset}
                        sx={{ textTransform: 'none', fontSize: 12, color: '#6366f1', ml: 'auto', flexShrink: 0 }}>
                        Reset Filters
                    </Button>
                    <Tooltip title={filtered.length === 0 ? 'No data to export' : someSelected ? `Export ${selected.length} selected` : `Export all ${filtered.length} filtered`}>
                        <span>
                            <Button size="small" variant="outlined" startIcon={<ExportIcon sx={{ fontSize: 15 }} />}
                                onClick={handleExportCSV} disabled={filtered.length === 0}
                                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderRadius: 1.5, flexShrink: 0,
                                    color: '#0891b2', borderColor: '#a5f3fc', bgcolor: '#ecfeff',
                                    '&:hover': { bgcolor: '#cffafe', borderColor: '#0891b2' },
                                    '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1', borderColor: '#e2e8f0' } }}>
                                {someSelected ? `Export (${selected.length})` : 'Export CSV'}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Paper>

            {/* ── Bulk Action Bar ── */}
            {someSelected && (
                <Paper elevation={0} sx={{ bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 2, p: 1.5, mb: 1.5, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#4338ca' }}>
                            {selected.length} timesheet{selected.length !== 1 ? 's' : ''} selected
                        </Typography>
                        {selectedActionableCount === 0 && (
                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                                None are pending review — you can still export them via Export CSV
                            </Typography>
                        )}
                        {selectedActionableCount > 0 && selectedActionableCount < selected.length && (
                            <Typography sx={{ fontSize: 11, color: '#6366f1' }}>
                                {selectedActionableCount} pending · {selected.length - selectedActionableCount} already processed (export only)
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <Button size="small" variant="contained" startIcon={<ApproveIcon sx={{ fontSize: 16 }} />} onClick={handleBulkApprove}
                            disabled={selectedActionableCount === 0}
                            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderRadius: 1.5, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, '&.Mui-disabled': { bgcolor: '#d1fae5', color: '#a7f3d0' } }}>
                            Approve ({selectedActionableCount})
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<RejectIcon sx={{ fontSize: 16 }} />} onClick={() => setBulkRejectOpen(true)}
                            disabled={selectedActionableCount === 0}
                            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderRadius: 1.5, color: '#dc2626', borderColor: '#fca5a5', '&:hover': { bgcolor: '#fef2f2' }, '&.Mui-disabled': { color: '#fca5a5', borderColor: '#fee2e2' } }}>
                            Reject ({selectedActionableCount})
                        </Button>
                        <Button size="small" onClick={() => setSelected([])} sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}>
                            Clear
                        </Button>
                    </Stack>
                </Paper>
            )}

            {/* ── Cards ── */}
            {loading ? (
                <Box>
                    {[...Array(3)].map((_, gi) => (
                        <Paper key={gi} elevation={0} sx={{ border: '1px solid #e2e8f0', borderLeft: '4px solid #c7d2fe', borderRadius: 2.5, mb: 1.5, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.75 }}>
                                <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 0.5, flexShrink: 0 }} />
                                <Skeleton variant="circular" width={38} height={38} sx={{ flexShrink: 0 }} />
                                <Box sx={{ minWidth: 120 }}>
                                    <Skeleton variant="text" width={120} height={16} />
                                    <Skeleton variant="text" width={80} height={12} />
                                </Box>
                                <Skeleton variant="rectangular" width={48} height={32} sx={{ borderRadius: 1.5 }} />
                                <Skeleton variant="rectangular" width={48} height={32} sx={{ borderRadius: 1.5 }} />
                                <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 2, ml: 1 }} />
                                <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 2 }} />
                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Skeleton variant="text" width={72} height={14} />
                                    <Skeleton variant="circular" width={28} height={28} />
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            ) : groups.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <EmptyIcon sx={{ fontSize: 52, color: '#c7d2fe', mb: 1.5 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>No Timesheets Found</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Try adjusting your search or filters.</Typography>
                </Box>
            ) : (
                <Box>
                    {/* Select-all row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, px: 0.5 }}>
                        <Checkbox size="small"
                            checked={allPageSelected}
                            indeterminate={somePageSelected && !allPageSelected}
                            onChange={toggleAll}
                            disabled={allPageIds.length === 0}
                            sx={{ color: '#1D4ED8', '&.Mui-checked': { color: '#1D4ED8' }, '&.MuiCheckbox-indeterminate': { color: '#1D4ED8' }, p: 0.5 }}
                        />
                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                            {allPageSelected ? 'Deselect all' : 'Select all on this page'} ({allPageIds.length})
                        </Typography>
                    </Box>

                    {/* Employee cards */}
                    {paginatedGroups.map((group, gIdx) => {
                        const expanded    = isExpanded(group.employeeId);
                        const avatarColor = AVATAR_COLORS[gIdx % AVATAR_COLORS.length];

                        const submittedCnt      = group.timesheets.filter(ts => ts.status === 'Submitted').length;
                        const pendingL2Cnt      = group.timesheets.filter(ts => ts.status === 'Pending Level 2').length;
                        const approvedCnt       = group.timesheets.filter(ts => ts.status === 'Approved').length;
                        const rejectedCnt       = group.timesheets.filter(ts => ts.status === 'Rejected').length;
                        const draftCnt          = group.timesheets.filter(ts => ts.status === 'Draft').length;
                        const lastSubmittedDate = group.timesheets
                            .map(ts => ts.submittedOn)
                            .filter(Boolean)
                            .sort()
                            .pop();

                        return (
                            <Paper key={group.employeeId} elevation={0} sx={{
                                border: '1px solid #e2e8f0',
                                borderLeft: `4px solid ${avatarColor}`,
                                borderRadius: 2.5,
                                mb: 1.5,
                                overflow: 'hidden',
                                transition: 'box-shadow 0.15s',
                                '&:hover': { boxShadow: '0 2px 12px rgba(99,102,241,0.08)' },
                            }}>

                                {/* ── Card Header ── */}
                                <Box
                                    onClick={() => toggleGroup(group.employeeId)}
                                    sx={{
                                        px: 2, py: 1.5,
                                        bgcolor: expanded ? '#f8faff' : '#fff',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: '#f0f4ff' },
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

                                        {/* Checkbox */}
                                        <Box onClick={e => e.stopPropagation()}>
                                            <Checkbox size="small"
                                                checked={isGroupAllSelected(group)}
                                                indeterminate={isGroupPartial(group)}
                                                disabled={group.timesheets.length === 0}
                                                onChange={() => toggleGroupSelection(group)}
                                                sx={{ color: '#4f46e5', '&.Mui-checked': { color: '#4f46e5' }, '&.MuiCheckbox-indeterminate': { color: '#4f46e5' }, p: 0.5 }}
                                            />
                                        </Box>

                                        {/* Avatar */}
                                        <Avatar sx={{ width: 38, height: 38, bgcolor: avatarColor, fontSize: 13, fontWeight: 700, borderRadius: 2, flexShrink: 0 }}>
                                            {group.employeeAvatar}
                                        </Avatar>

                                        {/* Name + dept */}
                                        <Box sx={{ minWidth: 130 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e293b', lineHeight: 1.2 }}>
                                                {group.employeeName}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                                                {group.employeeDept}{group.employeeTeam ? ` · ${group.employeeTeam}` : ''}
                                            </Typography>
                                        </Box>

                                        {/* Action badges */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 0.5, flexWrap: 'wrap' }}>
                                            {submittedCnt > 0 && (
                                                <Chip label={`${submittedCnt} pending review`} size="small"
                                                    sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700, fontSize: 11, height: 22, borderRadius: 1.5, border: '1px solid #93c5fd' }} />
                                            )}
                                            {globalConfig?.is_two_level_approval && pendingL2Cnt > 0 && (
                                                <Chip label={`${pendingL2Cnt} pending ext.`} size="small"
                                                    sx={{ bgcolor: '#ffedd5', color: '#c2410c', fontWeight: 700, fontSize: 11, height: 22, borderRadius: 1.5, border: '1px solid #fdba74' }} />
                                            )}
                                            {rejectedCnt > 0 && (
                                                <Chip label={`${rejectedCnt} rejected`} size="small"
                                                    sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 11, height: 22, borderRadius: 1.5, border: '1px solid #fca5a5' }} />
                                            )}
                                            {approvedCnt > 0 && (
                                                <Chip label={`${approvedCnt} approved`} size="small"
                                                    sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 11, height: 22, borderRadius: 1.5, border: '1px solid #86efac' }} />
                                            )}
                                            {draftCnt > 0 && (
                                                <Chip label={`${draftCnt} draft`} size="small"
                                                    sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: 11, height: 22, borderRadius: 1.5, border: '1px solid #cbd5e1' }} />
                                            )}
                                            {submittedCnt === 0 && pendingL2Cnt === 0 && rejectedCnt === 0 && (
                                                <Typography sx={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic' }}>
                                                    No pending review
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Last submitted */}
                                        {lastSubmittedDate && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CalendarIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                                                <Typography sx={{ fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                    Last submitted: {fmtDate(lastSubmittedDate)}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Timesheet count + expand toggle */}
                                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
                                                {group.timesheets.length} timesheet{group.timesheets.length !== 1 ? 's' : ''}
                                            </Typography>
                                            <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
                                                <IconButton size="small"
                                                    onClick={e => { e.stopPropagation(); toggleGroup(group.employeeId); }}
                                                    sx={{ color: '#6366f1', bgcolor: '#eef2ff', '&:hover': { bgcolor: '#e0e7ff' }, borderRadius: 1.5 }}>
                                                    {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* ── Timesheet rows ── */}
                                {expanded && (
                                    <Box sx={{ borderTop: '1px solid #e8eeff' }}>
                                        {/* Mobile / tablet: compact stacked rows instead of the 8-column table */}
                                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                            {group.timesheets.map((ts, idx) => {
                                                const isSelected    = selected.includes(ts.id);
                                                const billableRate  = ts.totalHours > 0 ? Math.round((ts.billableHours / ts.totalHours) * 100) : 0;
                                                const isLastInGroup = idx === group.timesheets.length - 1;
                                                const goToReview    = () => navigate(role === 'Manager' ? `/manager/timesheet-management/${ts.id}` : `/team-lead/my-timesheet/${ts.id}`, { state: { returnTab: 1 } });

                                                return (
                                                    <Box
                                                        key={ts.id}
                                                        onClick={goToReview}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            p: 1.5,
                                                            maxWidth: '100%',
                                                            overflow: 'hidden',
                                                            boxSizing: 'border-box',
                                                            borderLeft: `3px solid ${avatarColor}40`,
                                                            bgcolor: isSelected ? '#eef2ff' : '#fff',
                                                            borderBottom: isLastInGroup ? 'none' : '1px solid #f1f5f9',
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                            <Box onClick={e => e.stopPropagation()}>
                                                                <Checkbox size="small" checked={isSelected}
                                                                    onChange={() => toggleSelect(ts.id)}
                                                                    sx={{ p: 0.5, mt: -0.5, '&.Mui-checked': { color: '#6366f1' } }}
                                                                />
                                                            </Box>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                        <CalendarIcon sx={{ fontSize: 13, color: '#94a3b8', flexShrink: 0 }} />
                                                                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
                                                                            {fmtShort(ts.weekStart)} – {fmtShort(ts.weekEnd)}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ flexShrink: 0 }}>
                                                                        {!(ts.status === 'Submitted' && ts.resubmission_count) && (
                                                                            <StatusChip status={ts.status} />
                                                                        )}
                                                                        <ResubmittedBadge count={ts.resubmission_count} status={ts.status} />
                                                                    </Box>
                                                                </Stack>

                                                                <Stack direction="row" alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#6366f1' }}>{ts.totalHours}h reg</Typography>
                                                                    {ts.overtimeHours > 0 && (
                                                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>+{ts.overtimeHours}h OT</Typography>
                                                                    )}
                                                                    <Typography sx={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>{billableRate}% billable</Typography>
                                                                    <Chip label={`${ts.projectCount} proj`} size="small"
                                                                        sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 10.5, height: 18, borderRadius: 1 }} />
                                                                </Stack>

                                                                <LinearProgress variant="determinate" value={billableRate}
                                                                    sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#10b981', borderRadius: 2 } }}
                                                                />

                                                                <Divider sx={{ my: 1 }} />

                                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                                                    <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                                                                        {ts.submittedOn
                                                                            ? fmtDate(ts.submittedOn)
                                                                            : <span style={{ color: '#c4c4c4' }}>Not submitted</span>}
                                                                    </Typography>
                                                                    <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                                                                        <Tooltip title="Review Details">
                                                                            <IconButton size="small" onClick={goToReview}
                                                                                sx={{ color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' } }}>
                                                                                <ReviewIcon sx={{ fontSize: 17 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        {isActionableStatus(ts) && (<>
                                                                            <Tooltip title="Quick Approve">
                                                                                <IconButton size="small" onClick={() => handleQuickAction(ts, 'Approve')}
                                                                                    sx={{ color: '#059669', '&:hover': { bgcolor: '#ecfdf5' } }}>
                                                                                    <ApproveIcon sx={{ fontSize: 17 }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Reject (add comment)">
                                                                                <IconButton size="small" onClick={() => handleQuickAction(ts, 'Reject')}
                                                                                    sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}>
                                                                                    <RejectIcon sx={{ fontSize: 17 }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </>)}
                                                                    </Box>
                                                                </Stack>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                        <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
                                            <Table size="small" sx={{ minWidth: 700 }}>
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#f8faff' }}>
                                                        <TableCell padding="checkbox" sx={{ pl: 2, width: 40 }} />
                                                        {['Week Period', 'Reg Hrs', 'OT Hrs', 'Billable', 'Projects', 'Status', 'Submitted On', 'Actions'].map(h => (
                                                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#1D4ED8', py: 1, whiteSpace: 'nowrap' }}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {group.timesheets.map((ts, idx) => {
                                                        const isSelected    = selected.includes(ts.id);
                                                        const billableRate  = ts.totalHours > 0 ? Math.round((ts.billableHours / ts.totalHours) * 100) : 0;
                                                        const isLastInGroup = idx === group.timesheets.length - 1;

                                                        return (
                                                            <TableRow key={ts.id}
                                                                selected={isSelected}
                                                                onClick={() => navigate(role === 'Manager' ? `/manager/timesheet-management/${ts.id}` : `/team-lead/my-timesheet/${ts.id}`, { state: { returnTab: 1 } })}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    bgcolor: isSelected ? '#eef2ff' : '#fff',
                                                                    '&:hover': { bgcolor: '#f8faff !important' },
                                                                    '&.Mui-selected': { bgcolor: '#eef2ff !important' },
                                                                    '&.Mui-selected:hover': { bgcolor: '#e0e7ff !important' },
                                                                    '& .MuiTableCell-root': { borderBottom: isLastInGroup ? 'none' : '1px solid #f1f5f9' },
                                                                }}
                                                            >
                                                                {/* Checkbox */}
                                                                <TableCell padding="checkbox" sx={{ pl: 2, borderLeft: `3px solid ${avatarColor}40` }} onClick={e => e.stopPropagation()}>
                                                                    <Checkbox size="small" checked={isSelected}
                                                                        onChange={() => toggleSelect(ts.id)}
                                                                        sx={{ '&.Mui-checked': { color: '#6366f1' } }}
                                                                    />
                                                                </TableCell>

                                                                {/* Week Period */}
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                        <CalendarIcon sx={{ fontSize: 13, color: '#94a3b8', flexShrink: 0 }} />
                                                                        <Typography sx={{ fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
                                                                            {fmtShort(ts.weekStart)} – {fmtShort(ts.weekEnd)}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>

                                                                {/* Reg Hrs */}
                                                                <TableCell>
                                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6366f1' }}>
                                                                        {ts.totalHours}h
                                                                    </Typography>
                                                                </TableCell>

                                                                {/* OT Hrs */}
                                                                <TableCell>
                                                                    <Typography sx={{ fontSize: 13, fontWeight: ts.overtimeHours > 0 ? 700 : 400, color: ts.overtimeHours > 0 ? '#f59e0b' : '#94a3b8' }}>
                                                                        {ts.overtimeHours > 0 ? `+${ts.overtimeHours}h` : '—'}
                                                                    </Typography>
                                                                </TableCell>

                                                                {/* Billable */}
                                                                <TableCell sx={{ minWidth: 90 }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>{ts.billableHours}h</Typography>
                                                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#059669' }}>{billableRate}%</Typography>
                                                                    </Box>
                                                                    <LinearProgress variant="determinate" value={billableRate}
                                                                        sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#10b981', borderRadius: 2 } }}
                                                                    />
                                                                </TableCell>

                                                                {/* Projects */}
                                                                <TableCell>
                                                                    <Chip label={`${ts.projectCount} proj`} size="small"
                                                                        sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 11, height: 20, borderRadius: 1 }} />
                                                                </TableCell>

                                                                {/* Status */}
                                                                <TableCell>
                                                                    {!(ts.status === 'Submitted' && ts.resubmission_count) && (
                                                                        <StatusChip status={ts.status} />
                                                                    )}
                                                                    <ResubmittedBadge count={ts.resubmission_count} status={ts.status} />
                                                                </TableCell>

                                                                {/* Submitted On */}
                                                                <TableCell>
                                                                    <Typography sx={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                                        {ts.submittedOn
                                                                            ? fmtDate(ts.submittedOn)
                                                                            : <span style={{ color: '#c4c4c4' }}>Not submitted</span>}
                                                                    </Typography>
                                                                </TableCell>

                                                                {/* Actions */}
                                                                <TableCell onClick={e => e.stopPropagation()}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                                        <Tooltip title="Review Details">
                                                                            <IconButton size="small"
                                                                                onClick={e => { e.stopPropagation(); navigate(role === 'Manager' ? `/manager/timesheet-management/${ts.id}` : `/team-lead/my-timesheet/${ts.id}`, { state: { returnTab: 1 } }); }}
                                                                                sx={{ color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' } }}>
                                                                                <ReviewIcon sx={{ fontSize: 17 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        {isActionableStatus(ts) && (<>
                                                                            <Tooltip title="Quick Approve">
                                                                                <IconButton size="small" onClick={() => handleQuickAction(ts, 'Approve')}
                                                                                    sx={{ color: '#059669', '&:hover': { bgcolor: '#ecfdf5' } }}>
                                                                                    <ApproveIcon sx={{ fontSize: 17 }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Reject (add comment)">
                                                                                <IconButton size="small" onClick={() => handleQuickAction(ts, 'Reject')}
                                                                                    sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}>
                                                                                    <RejectIcon sx={{ fontSize: 17 }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </>)}
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </Paper>
                        );
                    })}

                    {/* Pagination */}
                    <Box sx={{ display: 'flex', alignItems: 'center', borderTop: '1px solid #e2e8f0', mt: 0.5, px: 1 }}>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8', flex: 1 }}>
                            {groups.length} employee{groups.length !== 1 ? 's' : ''} · {filtered.length} timesheet{filtered.length !== 1 ? 's' : ''}
                        </Typography>
                        <TablePagination
                            component="div"
                            count={groups.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            labelRowsPerPage="Employees per page:"
                            sx={{ border: 'none', '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 12 } }}
                        />
                    </Box>
                </Box>
            )}

            {/* ── Review Dialog ── */}
            <TimesheetReview
                open={!!reviewTs}
                onClose={() => setReviewTs(null)}
                timesheet={reviewTs}
                onAction={handleReviewAction}
            />

            {/* ── Bulk Reject Dialog ── */}
            <BulkRejectDialog
                open={bulkRejectOpen}
                count={selected.length}
                onConfirm={handleBulkReject}
                onClose={() => setBulkRejectOpen(false)}
            />

            {/* ── Snackbar ── */}
            <Snackbar open={snack.open} autoHideDuration={3500}
                onClose={() => setSnack(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnack(p => ({ ...p, open: false }))} severity={snack.severity} sx={{ borderRadius: 2 }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
